import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from './cn';
import { Spinner } from './Spinner';

type Variant = 'primary' | 'accent' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
}

const base =
  'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors duration-150 btn-focus disabled:opacity-50 disabled:cursor-not-allowed';

const variants: Record<Variant, string> = {
  primary: 'bg-navy-900 text-cream-100 hover:bg-navy-800 active:bg-navy-700',
  accent: 'bg-rose-500 text-cream-100 hover:bg-rose-400 active:bg-rose-600',
  ghost:
    'bg-transparent text-navy-900 border border-rose-300 hover:bg-cream-200 active:bg-cream-300',
  danger: 'bg-lacquer text-cream-100 hover:bg-lacquer/90 active:bg-lacquer/80',
};

const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { variant = 'primary', size = 'md', loading = false, fullWidth, className, children, disabled, ...rest },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className)}
        {...rest}
      >
        {loading && <Spinner size={size === 'lg' ? 'md' : 'sm'} />}
        <span>{children}</span>
      </button>
    );
  },
);
Button.displayName = 'Button';
